import { DisclaimerResponse } from '@/api/tax-guide';
import { Box, styled } from '@mui/system';
import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DOMPurify from 'dompurify';
import privacyCopyright from '@/assets/images/privacyoptions29x14.jpg';

export type FooterProps = {
  title?: string;
  disclaimerContent?: DisclaimerResponse;
};

const FooterComponent = styled('div')({
  position: 'fixed',
  padding: [2, 2],
  color: '#747476',
  borderTop: '1px solid #C9CAC8',
  width: '100vw',
  left: 0,
  bottom: 0,
  right: 0,
  zIndex: 1,
  backgroundColor: '#ffffff',
  WebkitFontSmoothing: 'subpixel-antialiased',
});

export const Footer = (props: FooterProps) => {
  const { disclaimerContent, title } = props;

  return (
    <>
      <footer data-testid={'footer'} aria-label="tax guide footer">
        {title}
        <Disclaimer disclaimerContent={disclaimerContent} />
      </footer>
    </>
  );
};

const Disclaimer = ({
  disclaimerContent,
}: {
  disclaimerContent?: DisclaimerResponse;
}) => {
  const [showFooter, setFooterVisibility] = useState(true);

  useEffect(() => {
    const showFooterDisclaimer = () => setFooterVisibility(true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        showFooterDisclaimer();
      }
    };

    window.addEventListener('focus', showFooterDisclaimer);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', showFooterDisclaimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (!disclaimerContent) return <></>;

  const santizedCopyright = DOMPurify.sanitize(disclaimerContent.copyright);
  const santizedFooter = DOMPurify.sanitize(disclaimerContent.footer);

  const handlePrivacyChoicesClick = () => {
    (
      window as Window & {
        OneTrust?: {
          ToggleInfoDisplay?: () => void;
        };
      }
    ).OneTrust?.ToggleInfoDisplay?.();
  };

  return (
    <div id="ft">
      <FooterComponent>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            whiteSpace: 'nowrap',
            overflowY: 'hidden',
          }}
          onClick={() => setFooterVisibility(isVisible => !isVisible)}
        >
          {showFooter ? (
            <ExpandMoreIcon sx={{ color: 'black' }} />
          ) : (
            <ExpandLessIcon sx={{ color: 'black' }} />
          )}
          <Box sx={{ paddingRight: 0.5, paddingTop: 0.5 }}>
            {showFooter ? 'Hide' : 'Show'} |
          </Box>
          <Box
            sx={{ paddingTop: 0.5 }}
            dangerouslySetInnerHTML={{
              __html: santizedCopyright,
            }}
          />
        </Box>
        {showFooter ? (
          <Box
            id="footer-disclaimer-content"
            sx={{ marginTop: -1, padding: [0.5, 1] }}
            dangerouslySetInnerHTML={{
              __html: santizedFooter,
            }}
          />
        ) : null}
        <div style={{ position: 'fixed', right: '165px', bottom: '3px' }}>
          <a href="https://www.morganstanley.com/privacy-pledge" target="blank">
            Privacy &amp; Cookies
          </a>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              right: '10px',
              bottom: '3px',
              position: 'fixed',
              cursor: 'pointer',
            }}
            onClick={handlePrivacyChoicesClick}
          >
            <div
              style={{
                color: '#006890',
                textDecoration: 'underline',
              }}
              data-analytics-link="Global Footer | Your Privacy Choices"
              className="cmp-navigation__item-link"
            >
              Your Privacy Choices
            </div>
            <img
              src={privacyCopyright}
              className="cmp-footer-nav-icon-image"
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>
      </FooterComponent>
    </div>
  );
};
